import { useEffect } from "react";
import { API } from "aws-amplify";
import * as Sentry from "@sentry/react";
import { disableSentry } from "./features";
import { ADDITIONAL_FIELDS_ALL } from "./shared/additional-fields";

export function useEffectAsync(asyncFunc, dependencies) {
  useEffect(() => {
    asyncFunc();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, dependencies);
}

const DISABLE_SENTRY = disableSentry();

export function captureError(defaultMessage, code, e) {
  if (DISABLE_SENTRY) return;
  let json;
  try {
    json = JSON.stringify(e);
  } catch {
    json = "Error serialization failed";
  }

  if (typeof e === "object" && e.errors != null) {
    //Amplify AppSync error
    Sentry.withScope((scope) => {
      scope.setExtra("errors", e.errors);
      scope.setExtra("json", json);
      Sentry.captureMessage(defaultMessage, {
        tags: {
          code: code,
        },
      });
    });
  } else if (e instanceof Error) {
    Sentry.withScope((scope) => {
      scope.setExtra("contextMessage", defaultMessage);
      scope.setExtra("json", json);
      Sentry.captureException(e, {
        tags: {
          code: code,
        },
      });
    });
  } else if (typeof e === "string") {
    Sentry.withScope((scope) => {
      scope.setExtra("contextMessage", defaultMessage);
      Sentry.captureMessage(e, {
        tags: {
          code: code,
        },
      });
    });
  } else {
    Sentry.withScope((scope) => {
      scope.setExtra("json", json);
      Sentry.captureMessage(defaultMessage, {
        tags: {
          code: code,
        },
      });
    });
  }
}

/*
  Amplify GraphQL API may return results as paginated.
  This helper does multiple requests until really all results are loaded or fail.
*/
export async function listAll(query, setName) {
  const itemsList = [];
  let gqlOperation = query;
  let lastNextToken;
  while (true) {
    const response = await API.graphql(gqlOperation);
    const result = response.data[setName];
    itemsList.push(result.items);
    if (result.nextToken) {
      if (itemsList.length > 50) {
        //Arbitary maxium count. If this hits, something is done wrong.
        throw new Error("Maxium query count exceeded");
      } else if (result.nextToken === lastNextToken) {
        //Should not never happen
        throw new Error("Next token repeated error");
      }
      lastNextToken = result.nextToken;

      gqlOperation = {
        ...gqlOperation,
        variables: {
          ...gqlOperation.variables,
          nextToken: result.nextToken,
        },
      };
    } else {
      break;
    }
  }

  return [].concat(...itemsList);
}

export function gtagPageView(title, path, event, enabled) {
  if (!enabled) return false;
  if (typeof window !== "undefined" && typeof window.gtag !== "undefined") {
    window.gtag("event", "page_view", {
      page_title: title,
      page_path: path,
    });
  }
  if (typeof window.dataLayer !== "undefined") {
    window.dataLayer.push({
      event: "urent_flow_event",
      urent_step: event,
      //urent_value: 0,
    });
  }
  return true;
}

// If VITE_HOSTING_URL_CUSTOMER exists, load img from that URL. Otherwise use
// relative path (on localhost for example)
export function getImgHostingUrl(imgPath) {
  if (!imgPath) return;
  if (import.meta.env.VITE_HOSTING_URL_CUSTOMER) {
    return `${import.meta.env.VITE_HOSTING_URL_CUSTOMER}${imgPath}`;
  }
  return imgPath;
}

export const getServiceDescription = (service, language) => {
  const fixedLanguage = getFixedLanguage(language); // use this fancy apparatus to get the correct language

  const hasTranslatedDescription = service?.descriptionT?.[fixedLanguage];
  if (hasTranslatedDescription) {
    return service?.descriptionT[fixedLanguage];
  }

  return service?.description;
};

// For saving Reservation
export const getFixedLanguageSet = (language) => {
  if (language === "se") {
    return "sv";
  } else {
    return language;
  }
};

// For getting Reservation
export const getFixedLanguage = (language) => {
  if (language === "sv") {
    return "se";
  } else {
    return language;
  }
};

export const getFieldValue = (field, language) => {
  const fieldData = ADDITIONAL_FIELDS_ALL.find((a) => a.key === field.key);

  if (
    field?.value === "true" ||
    field?.value === "false" ||
    field?.key === "transmission" ||
    field?.key === "wheels" ||
    field?.key === "powerType"
  ) {
    return fieldData?.optionsT?.[language]?.[field?.value];
  }
  if (field?.value === "undefined" || field?.value === "null") {
    return null;
  }
  return field?.value;
};
